interface AppConfig {
  apiUrl: string;
  appName: string;
  version: string;
  isDevelopment: boolean;
  isProduction: boolean;
  cloudFunctionsUrl: string;
}

// Project IDs for different environments - centralized for consistency
const PROJECT_IDS = {
  development: 'aiprep-465906',
  production: 'aiprep-prod',
} as const;

// Determine if we're in development (local vs deployed)
const isDevelopment =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname === "0.0.0.0";

// Check if we're running on Firebase hosting (dev environment)
const isDevEnvironment =
  window.location.hostname === "dev.aiprep.io" ||
  window.location.hostname.includes(PROJECT_IDS.development) ||
  window.location.hostname.includes("firebaseapp.com") ||
  window.location.hostname.includes("web.app");

// Check if we're in production environment
const isProductionEnvironment =
  window.location.hostname === "aiprep.io" ||
  window.location.hostname === "www.aiprep.io" ||
  window.location.hostname === "app.aiprep.io";

// Cloud Functions URLs - environment specific
const getCloudFunctionsUrl = (): string => {
  // Debug logging
  console.log("Environment Detection:", {
    hostname: window.location.hostname,
    isDevelopment,
    isDevEnvironment,
    isProductionEnvironment,
    useRealAPI: process.env.REACT_APP_USE_REAL_API
  });

  // Check if we should use real API even in development
  if (isDevelopment && process.env.REACT_APP_USE_REAL_API === 'true') {
    console.log("Using real Cloud Functions URL (REACT_APP_USE_REAL_API=true)");
    return `https://us-central1-${PROJECT_IDS.development}.cloudfunctions.net`;
  }

  if (isDevelopment) {
    // Local development - use emulator
    console.log("Using local emulator URL");
    return `http://127.0.0.1:5001/${PROJECT_IDS.development}/us-central1`;
  }
  if (isProductionEnvironment) {
    // Production environment - uses aiprep-prod project
    console.log("Using production Cloud Functions URL");
    return `https://us-central1-${PROJECT_IDS.production}.cloudfunctions.net`;
  }
  if (isDevEnvironment) {
    // Dev environment - uses aiprep-465906 project
    console.log("Using dev Cloud Functions URL");
    return `https://us-central1-${PROJECT_IDS.development}.cloudfunctions.net`;
  }
  // Fallback - use dev for unknown hostnames (safer "dev by default" approach)
  // This ensures staging, preview deployments, and QA environments don't accidentally hit production
  console.warn("Unknown hostname, using fallback Cloud Functions URL (development):", window.location.hostname);
  return `https://us-central1-${PROJECT_IDS.development}.cloudfunctions.net`;
};

const cloudFunctionsUrl = getCloudFunctionsUrl();

// Environment type detection
const getEnvironmentType = (): string => {
  if (isDevelopment) return 'local';
  if (isDevEnvironment) return 'development';
  if (isProductionEnvironment) return 'production';
  return 'unknown';
};

// Create app configuration - ONLY Cloud Functions mode now
export const config: AppConfig = {
  apiUrl: cloudFunctionsUrl,  // Always use Cloud Functions
  appName: "AIPrep",
  version: "1.0.0",
  isDevelopment,
  isProduction: !isDevelopment && (isDevEnvironment || isProductionEnvironment),
  cloudFunctionsUrl,
};

// Export individual config values for convenience
export const API_BASE_URL = config.apiUrl;
export const CLOUD_FUNCTIONS_URL = config.cloudFunctionsUrl;
export const APP_NAME = config.appName;
export const APP_VERSION = config.version;
export const ENVIRONMENT_TYPE = getEnvironmentType();

// Export environment detection helpers
export const IS_DEV_ENVIRONMENT = isDevEnvironment;
export const IS_PRODUCTION_ENVIRONMENT = isProductionEnvironment;
export const IS_LOCAL_DEVELOPMENT = isDevelopment;

// Debug function to log configuration
export const logConfig = (): void => {
  console.log("🔧 AIPrep Configuration:", {
    ...config,
    environmentType: getEnvironmentType(),
    currentHost: window.location.host,
    currentProtocol: window.location.protocol,
    isDevEnvironment,
    isProductionEnvironment,
    isDevelopment,
  });
  console.log(`🌍 Environment: ${getEnvironmentType().toUpperCase()}`);
  console.log(`🔗 API URL: ${config.apiUrl}`);
  console.log(`⚡ Cloud Functions URL: ${config.cloudFunctionsUrl}`);
};